import type { RowData } from "@tanstack/react-table";
import type { RecordColumnDef } from "@/components/ui/record-table/column-types";
import { EmptyValue } from "@/components/ui/record-table/empty-value";
import {
  formatDate,
  formatDateTime,
  formatDateTimeCompact,
} from "@/lib/formatting/dates";
import { formatCents } from "@/lib/formatting/currency";
import { CheckCircle, XCircle } from "@/components/icons";
import type { AccessorFn, BaseColumnConfig } from "./types";
import {
  resolveColumnId,
  resolveAccessorProps,
  resolveNumber, resolveBoolean,
} from "./types";

type DateFormat = "datetime" | "compact" | "date";

interface DateColumnConfig<TData extends RowData> extends BaseColumnConfig {
  accessor: keyof TData | AccessorFn<TData, number | undefined>;
  format?: DateFormat;
}

const DATE_FORMAT_WIDTH: Record<DateFormat, number> = {
  date: 110,
  compact: 175,
  datetime: 200,
};

export function createDateColumn<TData extends RowData>(
  config: DateColumnConfig<TData>,
): RecordColumnDef<TData, unknown> {
  const {
    id,
    accessor,
    header,
    size,
    meta,
    format = "datetime",
    enableSorting = true,
  } = config;
  return {
    id: id ?? resolveColumnId(accessor, header),
    ...resolveAccessorProps(accessor),
    header,
    size: size ?? DATE_FORMAT_WIDTH[format],
    meta,
    enableSorting,
    sortFn: "basic",
    cell: ({ row }) => {
      const value = resolveNumber(accessor, row.original);
      if (!value) return <EmptyValue />;
      const formatted =
        format === "date"
          ? formatDate(value)
          : format === "compact"
            ? formatDateTimeCompact(value)
            : formatDateTime(value);
      return (
        <span className="text-sm tabular-nums whitespace-nowrap">{formatted}</span>
      );
    },
  };
}

interface CurrencyColumnConfig<TData extends RowData> extends BaseColumnConfig {
  accessor: keyof TData | AccessorFn<TData, number | undefined>;
  valueIsInDollars?: boolean;
}

export function createCurrencyColumn<TData extends RowData>(
  config: CurrencyColumnConfig<TData>,
): RecordColumnDef<TData, unknown> {
  const {
    accessor,
    header,
    size,
    meta,
    valueIsInDollars = false,
    enableSorting = true,
  } = config;
  return {
    id: resolveColumnId(accessor, header),
    ...resolveAccessorProps(accessor),
    header,
    size: size ?? 100,
    meta: {
      headerClassName: "text-right",
      cellClassName: "text-right",
      ...meta,
    },
    enableSorting,
    sortFn: "basic",
    cell: ({ row }) => {
      const value = resolveNumber(accessor, row.original);
      if (value === undefined || value === null)
        return <EmptyValue />;
      const amount = valueIsInDollars ? value * 100 : value;
      return <span className="tabular-nums text-sm">{formatCents(amount)}</span>;
    },
  };
}

interface NumberColumnConfig<TData extends RowData> extends BaseColumnConfig {
  accessor: keyof TData | AccessorFn<TData, number | undefined>;
  locale?: boolean;
}

export function createNumberColumn<TData extends RowData>(
  config: NumberColumnConfig<TData>,
): RecordColumnDef<TData, unknown> {
  const {
    accessor,
    header,
    size,
    meta,
    locale = true,
    enableSorting = true,
  } = config;
  return {
    id: resolveColumnId(accessor, header),
    ...resolveAccessorProps(accessor),
    header,
    size: size ?? 80,
    meta: {
      headerClassName: "text-right",
      cellClassName: "text-right",
      ...meta,
    },
    enableSorting,
    sortFn: "basic",
    cell: ({ row }) => {
      const value = resolveNumber(accessor, row.original);
      if (value === undefined || value === null)
        return <EmptyValue />;
      return (
        <span className="tabular-nums text-sm">
          {locale ? value.toLocaleString() : value}
        </span>
      );
    },
  };
}

interface CountColumnConfig<TData extends RowData> extends BaseColumnConfig {
  accessor: keyof TData | AccessorFn<TData, number | undefined>;
  zeroAsDash?: boolean;
}

export function createCountColumn<TData extends RowData>(
  config: CountColumnConfig<TData>,
): RecordColumnDef<TData, unknown> {
  const {
    accessor,
    header,
    size,
    meta,
    zeroAsDash = true,
    enableSorting = true,
  } = config;
  return {
    id: resolveColumnId(accessor, header),
    ...resolveAccessorProps(accessor),
    header,
    size: size ?? 60,
    meta: {
      headerClassName: "text-right",
      cellClassName: "text-right",
      ...meta,
    },
    enableSorting,
    sortFn: "basic",
    cell: ({ row }) => {
      const value = resolveNumber(accessor, row.original);
      if (value === undefined || value === null)
        return <EmptyValue />;
      if (zeroAsDash && value === 0)
        return <EmptyValue />;
      return <span className="tabular-nums">{value}</span>;
    },
  };
}

interface BooleanColumnConfig<TData extends RowData> extends BaseColumnConfig {
  accessor: keyof TData | AccessorFn<TData, boolean | undefined>;
  trueIcon?: React.ComponentType<{ className?: string }>;
  falseIcon?: React.ComponentType<{ className?: string }>;
}

export function createBooleanColumn<TData extends RowData>(
  config: BooleanColumnConfig<TData>,
): RecordColumnDef<TData, unknown> {
  const {
    accessor,
    header,
    size,
    meta,
    trueIcon: TrueIcon = CheckCircle,
    falseIcon: FalseIcon = XCircle,
    enableSorting = true,
  } = config;
  return {
    id: resolveColumnId(accessor, header),
    ...resolveAccessorProps(accessor),
    header,
    size: size ?? 70,
    meta,
    enableSorting,
    cell: ({ row }) => {
      const value = resolveBoolean(accessor, row.original);
      if (value === undefined)
        return <EmptyValue />;
      return value ? (
        <TrueIcon className="h-4 w-4 text-success-strong" />
      ) : (
        <FalseIcon className="h-4 w-4 text-muted-foreground" />
      );
    },
  };
}
